import React, { FC, useMemo } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { useDistributedTokenDetails } from '@app/hooks/ryo/useDistributedTokenDetails';
import { usePairDetails } from '@app/hooks/pair/usePairDetails';
import { TokensPair } from '@app/components/TokensPair';
import { TokenIcon } from '@app/components/TokenIcon';
import { useTokenBalance } from '@app/hooks/swap/useTokenBalance';
import { formatUSD } from '@app/helpers/formatUSD';
import { useTokenPriceV4 } from '@app/hooks/token/useTokenPriceV4';
import { useVaults } from '@app/hooks/plugins/useVaults';
import { kFormatter2 } from '@app/helpers/format';

interface Props {
  index: number;
}

export const PendingTokenItem: FC<Props> = ({ index }) => {
  const { currentDistributionAmount, distributedTokenAddress } =
    useDistributedTokenDetails(index);
  const { data: pairData, isLoading: isLoadingPairData } = usePairDetails({
    id: distributedTokenAddress?.toLowerCase()
  });
  const { data: tokenData, isLoading: isLoadingTokenData } = useTokenBalance(
    distributedTokenAddress
  );
  const { data: vaults } = useVaults();

  const usdAmount = useTokenPriceV4(
    distributedTokenAddress,
    currentDistributionAmount
  );

  const data = useMemo(() => {
    if (!distributedTokenAddress) {
      return null;
    }

    const vaultData = vaults?.find(
      v => v.lpToken.id.toLowerCase() === distributedTokenAddress.toLowerCase()
    );

    if (vaultData) {
      return {
        token0: vaultData.token0,
        token1: vaultData.token1,
        usdAmount:
          currentDistributionAmount !== undefined
            ? vaultData.lpToken.lpTokenUSD * +currentDistributionAmount
            : 0
      };
    }

    if (pairData) {
      return {
        token0: pairData.token0,
        token1: pairData.token1
      };
    }

    if (tokenData) {
      return {
        token0: tokenData,
        token1: undefined
      };
    }

    return null;
  }, [
    pairData,
    tokenData,
    distributedTokenAddress,
    vaults,
    currentDistributionAmount
  ]);

  if (!data || currentDistributionAmount === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'whiteOpacity.6',
        background: 'gray.3',
        padding: '26px 16px',
        width: '100%'
      }}
    >
      <HStack>
        {data.token1 !== undefined ? (
          <TokensPair
            token0Symbol={data.token0.symbol}
            token1Symbol={data.token1.symbol}
          />
        ) : (
          <TokenIcon width="32px" height="32px" symbol={data.token0.symbol} />
        )}
        <Text fontSize="18px" fontWeight={400} lineHeight="26px" color="white">
          {currentDistributionAmount !== undefined
            ? kFormatter2(currentDistributionAmount)
            : '0'}{' '}
          {`${data.token0.symbol}`}
          {data.token1 !== undefined && ` - ${data.token1.symbol}`}{' '}
        </Text>
        <Text fontSize="18px" fontWeight={400} lineHeight="26px" color="gray.4">
          (
          {data.usdAmount !== undefined
            ? formatUSD.format(data.usdAmount)
            : usdAmount !== null
            ? formatUSD.format(usdAmount)
            : '$0.00'}
          )
        </Text>
      </HStack>
    </Box>
  );
};
